"use client";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { getCurrencies } from "@stadium/redux-utils/selectors/configSelector";

import { useCheckoutContext } from "@stadium/checkout/src/contexts/checkout";
import { formatPrice } from "@stadium/helpers/currency";

import Card from "@stadium/checkout/src/components/payment-options/cards/card";
import Button from "@stadium/checkout/src/components/shared/buttons";

import { USE_CORPORATE_BILLING } from "@stadium/checkout/src/constants/corporate-billing";
import { USE_WALLET_OPTIONS } from "@stadium/checkout/src/constants/wallet";

import style from "./main.module.scss";

const { useWalletMoney: useWalletObj, useGlobalPoint: useGlobalPointObj } = USE_WALLET_OPTIONS;
const { corporateBilled } = USE_CORPORATE_BILLING;

const ReviewPaymentMethods = ({
  teamWallet = {},
  creditCard = {},
  orderTotal,
  showBillingSection = true,
  storeDetails = {},
  showBillingNote = false,
  onEdit = null,
  isSingleSourcePayment = false,
} = {}) => {
  const {
    useStadiumPoints,
    stadiumPointsBalance,
    formattedStadiumPointsBalance,
    useWallet,
    userWalletBalance,
    walletCurrency,
    billingCountry,
    billingZipCode,
    billingNote,
    billingType,
    useStoreSpecificPoints,
    storeSpecificPointsBalance,
  } = useCheckoutContext();

  const { storeCurrencyName, storeCurrencyCode, storeCurrencyPrefix, storeCurrencySuffix } =
    storeDetails;

  const isWalletOrPointsSelected =
    (useStadiumPoints && stadiumPointsBalance) || (useWallet && userWalletBalance);

  const currencies = useConfig(getCurrencies);

  const currencyMapping = useMemo(() => {
    const mapping = {};
    currencies.forEach((currency) => {
      mapping[currency.code] = currency;
    });
    return mapping;
  }, [currencies]);

  const renderWalletLabel = (name, balance) => (
    <div className={style.billingBlock}>
      <div className={`${style.largeHeading} ${style.walletHead}`}>{name}</div>
      <div className={`${style.smallHeading} ${style.balanceText}`}>
        Available Balance: {balance}
      </div>
    </div>
  );

  const renderStadiumPoints = () => {
    if (!useStadiumPoints || !stadiumPointsBalance) return null;
    return renderWalletLabel(useGlobalPointObj.label, formattedStadiumPointsBalance);
  };

  const renderStoreSpecificPoints = () => {
    if (!useStoreSpecificPoints || !storeSpecificPointsBalance) return null;
    return renderWalletLabel(
      `Shop ${storeCurrencyName}`,
      formatPrice(storeSpecificPointsBalance, {
        prefix: storeCurrencyPrefix,
        suffix: storeCurrencySuffix,
        currencyCode: storeCurrencyCode,
      }),
    );
  };

  const renderMyWallet = () => {
    if (!useWallet || !userWalletBalance) return null;

    const currencyCode = walletCurrency.code;
    const prefix = currencyMapping[currencyCode]?.symbol || currencyCode,
      balance = formatPrice(userWalletBalance || 0, { prefix });

    return renderWalletLabel(useWalletObj.label, balance);
  };

  const renderTeamWallet = () => {
    if (isEmpty(teamWallet)) return null;
    const { name, currencyCode, myWalletBalance } = teamWallet;

    const prefix = currencyMapping[currencyCode]?.symbol || currencyCode,
      balance = formatPrice(myWalletBalance || 0, { prefix });

    return renderWalletLabel(
      `Wallet: ${name}`,
      balance,
    );
  };

  const renderCreditCard = () => {
    if (isEmpty(creditCard) || (!orderTotal && !isSingleSourcePayment)) return null;

    return (
      <div className={`${style.billingBlock} ${style.cardDetailBlock}`}>
        {isWalletOrPointsSelected && (
          <div className={`${style.smallHeading} ${style.creditCard}`}>
            We'll charge the rest to:{" "}
          </div>
        )}
        <Card card={creditCard} />
      </div>
    );
  };

  const renderCorporateBilling = () => {
    if (billingType !== corporateBilled.key) return null;
    return <p>Corporate Billing</p>;
  };

  const renderBillingSection = () =>
    !isEmpty(billingCountry) && (
      <div className={`${style.billingBlock} ${style.separatorBlock}`}>
        <div>
          <div className={style.smallHeading}>Country, Zip Code</div>
          <div className={style.largeHeading}>
            {billingCountry?.name}, {billingZipCode}
          </div>
        </div>
        {billingNote && (
          <div className={style.billingNoteBlock}>
            <div className={style.smallHeading}>Billing Note</div>
            <div className={style.largeHeading}>{billingNote}</div>
          </div>
        )}
      </div>
    );

  const renderBillingNote = () =>
    billingNote && (
      <div className={`${style.billingBlock} ${style.separatorBlock}`}>
        <div className={style.billingNoteBlock}>
          <div className={style.smallHeading}>Billing Note</div>
          <div className={style.largeHeading}>{billingNote}</div>
        </div>
      </div>
    );

  return (
    <div className={style.reviewDetailWrapper}>
      <div className={style.headingBlock}>
        <div className={style.headingContent}>
          <i className={`icon ${style.paymentIcon}`} />
          <span>Payment</span>
          {onEdit ? (
            <Button styleType="link" onClick={onEdit} className={style.editLink}>
              Edit
            </Button>
          ) : null}
        </div>
      </div>
      <div className={style.reviewPayInfo}>
        {renderStadiumPoints()}
        {renderStoreSpecificPoints()}
        {renderMyWallet()}
        {renderTeamWallet()}
        {renderCreditCard()}
        {renderCorporateBilling()}
        {showBillingSection && renderBillingSection()}
        {!showBillingSection && showBillingNote && renderBillingNote()}
      </div>
    </div>
  );
};

export default ReviewPaymentMethods;
